import { MEDALS, type Medal, type MedalTallyRow } from "@seagames/contracts";
import { useTranslation } from "react-i18next";
import { formatInteger } from "../../lib/i18n/format";
import { type TallySort, cellOf } from "../../lib/medals/tally";
import { Sparkline } from "../charts/sparkline";
import { MEDAL_COLOR, MedalDisc } from "../medals/medal-chip";
import { NocLabel } from "../medals/noc-label";

const COLOUR_SORT: Record<Medal, TallySort> = { GOLD: "gold", SILVER: "silver", BRONZE: "bronze" };

/**
 * The tally as a table: the API's rank ("=2" when shared), the NOC, gold /
 * silver / bronze / total, a mini G / S / B bar (on the scale of the biggest
 * total) and the medals-so-far sparkline over the games days. A colour
 * header re-sorts by that colour (`aria-sort`); the rank column restores the
 * standing. A row opens the NOC's drawer.
 */
export function TallyTable({
	rows,
	shared,
	days,
	sort,
	onSort,
	onOpen,
}: {
	rows: readonly MedalTallyRow[];
	shared: ReadonlySet<number>;
	/** The games days the sparkline runs over (the day breakdown's keys). */
	days: readonly string[];
	sort: TallySort;
	onSort: (sort: TallySort) => void;
	onOpen: (row: MedalTallyRow) => void;
}) {
	const { t } = useTranslation("tally");
	const tm = useTranslation("medals").t;
	const max = Math.max(1, ...rows.map((r) => r.total));
	const head = (key: TallySort, label: React.ReactNode, extra = "") => (
		<th scope="col" aria-sort={sort === key ? "descending" : undefined} className={`px-[0.75rem] py-[0.5rem] text-right ${extra}`}>
			<button type="button" onClick={() => onSort(sort === key && key !== "rank" ? "rank" : key)} className="inline-flex items-center gap-[0.375rem] uppercase hover:text-ink">
				{label}
				{sort === key && key !== "rank" && <span aria-hidden="true">▼</span>}
			</button>
		</th>
	);

	return (
		<div className="overflow-x-auto">
			<table className="w-full min-w-[44rem] text-[0.9375rem]">
				<thead className="border-rule border-b bg-paper text-[0.75rem] text-muted tracking-wide">
					<tr>
						{head("rank", t("table.rank"))}
						<th scope="col" className="px-[0.75rem] py-[0.5rem] text-left uppercase">
							{t("table.noc")}
						</th>
						{MEDALS.map((m) =>
							head(
								COLOUR_SORT[m],
								<>
									<MedalDisc medal={m} />
									{tm(`medal.${m}`)}
								</>,
							),
						)}
						{head("total", t("table.total"))}
						<th scope="col" className="hidden px-[0.75rem] py-[0.5rem] text-left uppercase md:table-cell">
							{t("table.mix")}
						</th>
						<th scope="col" className="hidden px-[0.75rem] py-[0.5rem] text-left uppercase lg:table-cell">
							{t("table.trend")}
						</th>
					</tr>
				</thead>
				<tbody className="font-mono">
					{rows.map((r) => {
						let run = 0;
						const trend = days.map((d) => {
							run += cellOf(r, d).total;
							return run;
						});
						return (
							<tr key={r.noc.responsible_organization_id} className={`border-rule border-b last:border-b-0 hover:bg-paper ${r.rank === 1 && r.total > 0 ? "bg-cursor-wash/60" : ""}`}>
								<td className="px-[0.75rem] py-[0.5rem] text-right font-bold">
									{shared.has(r.rank) ? `=${formatInteger(r.rank)}` : formatInteger(r.rank)}
								</td>
								<td className="max-w-[18rem] px-[0.75rem] py-[0.375rem] font-sans">
									<button
										type="button"
										onClick={() => onOpen(r)}
										aria-label={t("table.open", { noc: r.noc.country_name ?? r.noc.name })}
										className="flex min-h-[2.25rem] w-full min-w-0 items-center text-left text-ink hover:underline"
									>
										<NocLabel noc={r.noc} />
									</button>
								</td>
								<td className="px-[0.75rem] py-[0.5rem] text-right">{formatInteger(r.gold)}</td>
								<td className="px-[0.75rem] py-[0.5rem] text-right">{formatInteger(r.silver)}</td>
								<td className="px-[0.75rem] py-[0.5rem] text-right">{formatInteger(r.bronze)}</td>
								<td className="px-[0.75rem] py-[0.5rem] text-right font-bold">{formatInteger(r.total)}</td>
								<td className="hidden px-[0.75rem] py-[0.5rem] md:table-cell" aria-hidden="true">
									<span className="flex h-[0.625rem] w-[8rem] gap-[2px]">
										<span className="flex h-full gap-[2px]" style={{ width: `${(r.total / max) * 100}%` }}>
											{(["GOLD", "SILVER", "BRONZE"] as const)
												.filter((m) => (m === "GOLD" ? r.gold : m === "SILVER" ? r.silver : r.bronze) > 0)
												.map((m, i, all) => (
													<span
														key={m}
														className={`h-full ${i === all.length - 1 ? "rounded-r-[3px]" : ""}`}
														style={{ flexGrow: m === "GOLD" ? r.gold : m === "SILVER" ? r.silver : r.bronze, flexBasis: 0, background: MEDAL_COLOR[m] }}
													/>
												))}
										</span>
									</span>
								</td>
								<td className="hidden px-[0.75rem] py-[0.5rem] lg:table-cell">
									<Sparkline values={trend} />
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}
